import { FC, useCallback, useEffect, useState, MouseEvent } from 'react';
import { Alert, Box, Button, Select, MenuItem, SelectChangeEvent, TextField, CircularProgress, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import StorylineServerProxy, { GmailBulkDraftResult, GmailConnectionStatus } from "../storyline-proxy/storyline-proxy";
import Customer from './customer';

export interface ReportGeneratorProps{
    loggedInUser: string,
}

export const ReportGenerator: FC<ReportGeneratorProps> = ({ loggedInUser }) =>  {

    let [users, setUsers] = useState([] as string[]);
    let [selectedUser, setSelectedUser] = useState(loggedInUser);
    let [signature, setSignature] = useState("<No email signature found>");
    let [customers, setCustomers] = useState([] as Customer[]);
    let [selectedCustomer, setSelectedCustomer] = useState("");

    let [firstDate, setFirstDate] = useState(new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10));
    let [lastDate, setLastDate] = useState(new Date().toISOString().substring(0, 10));

    let [inProgress, setInProgress] = useState(false);
    const [gmailStatus, setGmailStatus] = useState<GmailConnectionStatus | null>(null);
    const [gmailStatusLoading, setGmailStatusLoading] = useState(true);
    const [gmailOperation, setGmailOperation] = useState<"connect" | "disconnect" | "single" | "bulk" | null>(null);
    const [gmailMessage, setGmailMessage] = useState("");
    const [gmailError, setGmailError] = useState("");
    const [bulkResults, setBulkResults] = useState<GmailBulkDraftResult[]>([]);

    const errorMessage = (error: unknown) => error instanceof Error ? error.message : String(error);

    const refreshGmailStatus = useCallback(async () => {
        setGmailStatusLoading(true);
        try{
            setGmailStatus(await StorylineServerProxy.gmailStatus());
            setGmailError("");
        }
        catch(error){
            setGmailError(`Could not check Gmail connection: ${errorMessage(error)}`);
        }
        finally{
            setGmailStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshGmailStatus();
        const refreshAfterOAuth = () => refreshGmailStatus();
        window.addEventListener("focus", refreshAfterOAuth);
        return () => window.removeEventListener("focus", refreshAfterOAuth);
    }, [loggedInUser, refreshGmailStatus]);

    useEffect(() => {
        StorylineServerProxy.getUsers()?.then((users) => {
            let usernames = ["all"];
            for(let user of users){
                if(user.roles.includes("FoxATM")){
                    usernames.push(user.username);
                    if(user.username == loggedInUser){
                        setSignature(user.emailSignature);
                    }                        
                }
            }
            setUsers(usernames);
            setSelectedUser(loggedInUser);
        })
    }, [loggedInUser]);

    useEffect(() => {
        StorylineServerProxy.getCustomers()?.then((customers) => {
            let allCustomers = customers;
            if(selectedUser == "all"){
                setCustomers(allCustomers);
                setSelectedCustomer(allCustomers[0]?.name || "");
            }
            else{
                let newCustomers: Customer[] = [];
                for(let customer of allCustomers){
                    if(customer.lead == selectedUser){
                        newCustomers.push(customer);
                    }
                }
                setCustomers(newCustomers);
                setSelectedCustomer(newCustomers[0]?.name || "");
            }
        })
    },[selectedUser]);

    useEffect(() => {
        StorylineServerProxy.getCustomers()?.then(customers => {
            for(const customer of customers){
                if(customer.name == selectedCustomer){
                const interval = customer.interval?customer.interval:7;
                    setFirstDate((new Date(new Date().getTime() - interval * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)));
                    setLastDate((new Date(new Date().getTime()).toISOString().substring(0, 10)));
                }
            }
        })
    }, [selectedCustomer]);

    const handleUserChange = (event: SelectChangeEvent) => {
        setSelectedUser(event.target.value as string);
    };

    const handleCustomerChange = (event: SelectChangeEvent) => {
        setSelectedCustomer(event.target.value as string);
    };

    const generateEmail = (customer: Customer) => {
        let mailtoStringEncoded = encodeURI("mailto:" + customer.emails + 
        "?cc=" + customer.emailsCC + "&subject=" + customer.emailTitle + "&body=") + encodeURIComponent(customer.emailBody) + encodeURIComponent("\n") + encodeURIComponent(signature);
        window.open(mailtoStringEncoded);
    }

    const handleGenerateEmail = (event: MouseEvent) => {
        for(let customer of customers){
            if(customer.name == selectedCustomer){
                generateEmail(customer);
                break;
            }
        }
    }

    const handleGenerateAllEmails = () => {
        for(let customer of customers){
            generateEmail(customer);
        }
    }

    const handleGmailConnect = async () => {
        setGmailOperation("connect");
        setGmailMessage("");
        setGmailError("");
        const oauthWindow = window.open("", "storyline-gmail-oauth", "popup,width=600,height=750");
        try{
            const { authorizationUrl } = await StorylineServerProxy.startGmailOAuth();
            if(oauthWindow){
                oauthWindow.location.href = authorizationUrl;
                oauthWindow.focus();
                const checkClosed = window.setInterval(() => {
                    if(oauthWindow.closed){
                        window.clearInterval(checkClosed);
                        refreshGmailStatus();
                    }
                }, 500);
            }
            else{
                window.open(authorizationUrl, "_blank");
                setGmailMessage("Google authorization opened in a new tab. Refresh the Gmail status after connecting.");
            }
        }
        catch(error){
            oauthWindow?.close();
            setGmailError(`Could not start Gmail connection: ${errorMessage(error)}`);
        }
        finally{
            setGmailOperation(null);
        }
    }

    const handleGmailDisconnect = async () => {
        setGmailOperation("disconnect");
        setGmailMessage("");
        setGmailError("");
        try{
            setGmailStatus(await StorylineServerProxy.disconnectGmail());
            setGmailMessage("Gmail disconnected.");
        }
        catch(error){
            setGmailError(`Could not disconnect Gmail: ${errorMessage(error)}`);
        }
        finally{
            setGmailOperation(null);
        }
    }

    const handleCreateGmailDraft = async () => {
        if(!selectedCustomer){
            return;
        }
        setGmailOperation("single");
        setGmailMessage("");
        setGmailError("");
        setBulkResults([]);
        try{
            const result = await StorylineServerProxy.createGmailDraft(selectedCustomer, firstDate, lastDate);
            setGmailMessage(`Gmail draft created for ${result.customer} with ${result.fileName} attached. Review it in Gmail Drafts before sending.`);
        }
        catch(error){
            setGmailError(`Could not create the Gmail draft: ${errorMessage(error)}`);
        }
        finally{
            setGmailOperation(null);
        }
    }

    const handleCreateAllGmailDrafts = async () => {
        const customerNames = customers
            .filter(customer => Boolean(customer.emails?.trim()))
            .map(customer => customer.name);
        if(customerNames.length === 0){
            return;
        }
        setGmailOperation("bulk");
        setGmailMessage("");
        setGmailError("");
        setBulkResults([]);
        try{
            const { results } = await StorylineServerProxy.createGmailDrafts(customerNames);
            setBulkResults(results);
            const created = results.filter(result => result.ok).length;
            const failed = results.length - created;
            setGmailMessage(`${created} of ${results.length} Gmail drafts created${failed ? `; ${failed} failed` : ""}. Review the drafts in Gmail before sending.`);
        }
        catch(error){
            setGmailError(`Could not create Gmail drafts: ${errorMessage(error)}`);
        }
        finally{
            setGmailOperation(null);
        }
    }

    const gmailDraftsUrl = gmailStatus?.email
        ? `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(gmailStatus.email)}#drafts`
        : "https://mail.google.com/mail/#drafts";

    const eligibleCustomerCount = customers.filter(customer => Boolean(customer.emails?.trim())).length;

    const handleGenerateAllReports = async () => {
        let fileName = await StorylineServerProxy.reportsForLead(selectedUser);
        window.open(new URL(StorylineServerProxy.getURLBase()).origin + "/reports/" + fileName.fileName);

    }

    const handleDownloadReport = async (event: MouseEvent) => {
        console.log("Will call for " + selectedCustomer + " " + firstDate + " " + lastDate);
        setInProgress(true);
        let fileName = await StorylineServerProxy.reportForCustomer(selectedCustomer, firstDate, lastDate);
        console.log(fileName);
        window.open(new URL(StorylineServerProxy.getURLBase()).origin + "/reports/" + fileName.fileName);
        setInProgress(false);
    }



    return (
        <>
            <Select onChange={ handleUserChange } value={ selectedUser }>
                { 
                    users.map((currentUser) => {
                        return <MenuItem key={currentUser} value={currentUser}>{ currentUser }</MenuItem>
                    })
                }
            </Select>
            <Select onChange={ handleCustomerChange } value={ selectedCustomer }>
                { 
                    customers.map((customer) => {
                        return <MenuItem key={customer.name} value={customer.name}>{ customer.name }</MenuItem>
                    })
                }
            </Select>
            <br/><br/>
            <TextField sx={{ width: 1/2 }} label={"First date"} value={firstDate} onChange={event => {setFirstDate(event.target.value)}}/>
            <TextField sx={{ width: 1/2 }} label={"Last date"} value={lastDate} onChange={event => {setLastDate(event.target.value)}}/>
            {
                inProgress?<CircularProgress/>:<Button onClick={handleDownloadReport}>Download report</Button>
            }
            <Button onClick={handleGenerateEmail}>Generate email</Button>
            <br/><br/>
            <Button onClick={handleGenerateAllReports}>Generate all reports</Button>
            <Button onClick={handleGenerateAllEmails}>Generate all emails</Button>

            <Paper variant="outlined" sx={{ marginTop: 3, padding: 2 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h6">Gmail drafts with report attachments</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Storyline only creates drafts. Open Gmail Drafts to review and send them yourself.
                        </Typography>
                    </Box>

                    {gmailStatusLoading
                        ? <Box><CircularProgress size={20} /> Checking Gmail connection…</Box>
                        : gmailStatus?.connected
                            ? <Alert severity="success">Connected as {gmailStatus.email || loggedInUser}</Alert>
                            : <Alert severity="info">Gmail is not connected for {loggedInUser}.</Alert>}

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                            variant={gmailStatus?.connected ? "outlined" : "contained"}
                            onClick={handleGmailConnect}
                            disabled={gmailOperation !== null}
                        >
                            {gmailOperation === "connect" ? <CircularProgress size={20} /> : gmailStatus?.connected ? "Reconnect Gmail" : "Connect Gmail"}
                        </Button>
                        {gmailStatus?.connected &&
                            <Button color="warning" onClick={handleGmailDisconnect} disabled={gmailOperation !== null}>
                                {gmailOperation === "disconnect" ? <CircularProgress size={20} /> : "Disconnect Gmail"}
                            </Button>}
                        <Button onClick={refreshGmailStatus} disabled={gmailStatusLoading || gmailOperation !== null}>Refresh status</Button>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                            variant="contained"
                            onClick={handleCreateGmailDraft}
                            disabled={!gmailStatus?.connected || !selectedCustomer || gmailOperation !== null}
                        >
                            Create Gmail draft
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateAllGmailDrafts}
                            disabled={!gmailStatus?.connected || eligibleCustomerCount === 0 || gmailOperation !== null}
                        >
                            Create all Gmail drafts ({eligibleCustomerCount})
                        </Button>
                        <Button href={gmailDraftsUrl} target="_blank" rel="noopener noreferrer">Open Gmail Drafts</Button>
                    </Stack>

                    {gmailOperation === "single" && <Box><LinearProgress /><Typography variant="body2">Creating the draft and report attachment for {selectedCustomer}…</Typography></Box>}
                    {gmailOperation === "bulk" && <Box><LinearProgress /><Typography variant="body2">Creating drafts and report attachments for {eligibleCustomerCount} customers…</Typography></Box>}
                    {gmailMessage && <Alert severity={bulkResults.some(result => !result.ok) ? "warning" : "success"}>{gmailMessage}</Alert>}
                    {gmailError && <Alert severity="error">{gmailError}</Alert>}
                    {bulkResults.filter(result => !result.ok).map(result =>
                        <Alert key={result.customer} severity="error">{result.customer}: {result.error || "Draft creation failed"}</Alert>
                    )}
                </Stack>
            </Paper>
        </>
    )
}
